import { useEffect, useRef, useState } from "react";
import { toString as qrToSvgString } from "qrcode";
import { Smartphone } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { InfoBanner } from "@/components/ui/banner";
import { SupportDetails } from "@/components/ui/copyable-reference";
import {
  createSelfSignerPairing,
  createSignerPairing,
  withStepUpRetry,
  type SignerPairingResult,
} from "@/lib/api";
import { useMobileSigningLocale } from "@/i18n/use-mobile-signing-locale";

/**
 * "Pair a signer device" (ALL-4884, MOBILE-SIGNING-CONTRACT.md §"Identity
 * and auth" #1). Calls the real POST /v1/signer/pairings through
 * withStepUpRetry (the same AAL2 step-up helper approve/reject use,
 * ALL-5022) — never a mocked code. A machine/service principal is refused
 * server-side with PRINCIPAL_NOT_HUMAN; the UI hides the control for that
 * case up front and explains why, per the contract's
 * "Machine principals never approve or sign" rule.
 */

type PairingPhase = "idle" | "loading" | "ready" | "expired" | "error";

interface PairingSuccess {
  pairingId?: string;
  pairingCode: string;
  expiresAt: string;
}

function secondsRemaining(expiresAt: string): number {
  const ms = new Date(expiresAt).getTime() - Date.now();
  return Number.isFinite(ms) ? Math.max(0, Math.floor(ms / 1000)) : 0;
}

function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Renders the pairing code as a scannable QR code (inline SVG, no network
 * call — the `qrcode` package encodes entirely client-side). The pairing
 * code is short-lived and single-use, so nothing about it is sent to a
 * third-party QR-rendering service. */
function PairingQrCode({ value }: { value: string }) {
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    qrToSvgString(value, { type: "svg", margin: 1, width: 176 })
      .then((markup) => { if (!cancelled) setSvg(markup); })
      .catch(() => { if (!cancelled) setSvg(null); });
    return () => { cancelled = true; };
  }, [value]);

  if (!svg) {
    return <div className="size-[176px] shrink-0 rounded-lg border border-line bg-bg" aria-hidden />;
  }
  // eslint-disable-next-line react/no-danger -- markup is generated locally by `qrcode`, not user input.
  return (
    <div
      className="size-[176px] shrink-0 overflow-hidden rounded-lg border border-line bg-white p-1.5"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

/**
 * `mode="self"` (D714, ALL-5472) is the invited signer's own "Pair my phone":
 * it calls POST /v1/signer/pairings/self, which names nobody — the gateway
 * binds the code to the signed-in principal — and explains the three
 * self-service refusals in plain words. `principalId` is ignored in that mode.
 */
const SELF_REFUSALS = ["SIGNER_DEVICE_ALREADY_BOUND", "SELF_PAIRING_DISABLED", "SIGNER_ROLE_REQUIRED"] as const;

export function PairSignerDevice({
  principalId,
  isHuman,
  canManage,
  mode = "admin",
}: {
  principalId: string;
  isHuman: boolean;
  canManage: boolean;
  mode?: "admin" | "self";
}) {
  const catalog = useMobileSigningLocale();
  const t = mode === "self" ? { ...catalog.pairing, ...catalog.selfPairing } : catalog.pairing;
  const [phase, setPhase] = useState<PairingPhase>("idle");
  const [pairing, setPairing] = useState<PairingSuccess | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [denial, setDenial] = useState<{ error: string; supportReference?: string; capability?: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!pairing) return;
    const initial = secondsRemaining(pairing.expiresAt);
    setRemaining(initial);
    if (initial <= 0) {
      setPhase("expired");
      return;
    }
    tickRef.current = setInterval(() => {
      const next = secondsRemaining(pairing.expiresAt);
      setRemaining(next);
      if (next <= 0) setPhase("expired");
    }, 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [pairing]);

  if (!isHuman) {
    return (
      <Card dir={catalog.dir}>
        <CardBody>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="size-4 text-primary" /> {t.title}
          </CardTitle>
          <p className="mt-3 text-sm text-muted" data-testid="pair-signer-device-machine-blocked">
            {t.machineBlocked}
          </p>
        </CardBody>
      </Card>
    );
  }

  if (!canManage) {
    return (
      <Card dir={catalog.dir}>
        <CardBody>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="size-4 text-primary" /> {t.title}
          </CardTitle>
          <p className="mt-3 text-xs text-muted">{t.description}</p>
        </CardBody>
      </Card>
    );
  }

  function onSuccess(result: SignerPairingResult) {
    if (result.pairingCode && result.expiresAt) {
      setPairing({ pairingId: result.pairingId, pairingCode: result.pairingCode, expiresAt: result.expiresAt });
      setPhase("ready");
      setDenial(null);
      setCopied(false);
    } else {
      // The backend answered 2xx without the fields the contract promises —
      // treat that as an error rather than rendering an empty/garbled code.
      setPhase("error");
      setDenial({ error: t.genericError, supportReference: result.supportReference, capability: result.capability });
    }
  }

  async function handlePair() {
    setPhase("loading");
    setDenial(null);
    const outcome = await withStepUpRetry(() =>
      mode === "self" ? createSelfSignerPairing() : createSignerPairing(principalId),
    );
    if (outcome.result.ok) {
      onSuccess(outcome.result);
      return;
    }
    if (outcome.stepUpFailed) {
      setPhase("error");
      setDenial({ error: t.stepUpFailed, supportReference: outcome.stepUpSupportReference });
      return;
    }
    setPhase("error");
    const reason = outcome.result.reasonCode;
    if (reason === "AUTH_ASSURANCE_INSUFFICIENT") {
      // The session itself is below AAL2, so no step-up was attempted
      // (withStepUpRetry): same guidance, with the pairing's own reference.
      setDenial({ error: t.stepUpFailed, supportReference: outcome.result.supportReference });
      return;
    }
    if (mode === "self" && reason && (SELF_REFUSALS as readonly string[]).includes(reason)) {
      const self = catalog.selfPairing;
      setDenial({
        error: reason === "SIGNER_DEVICE_ALREADY_BOUND"
          ? self.alreadyBound
          : reason === "SELF_PAIRING_DISABLED" ? self.disabled : self.roleRequired,
        supportReference: outcome.result.supportReference,
      });
      return;
    }
    setDenial({
      error: outcome.result.error || t.genericError,
      supportReference: outcome.result.supportReference,
      capability: outcome.result.capability,
    });
  }

  async function handleCopy() {
    if (!pairing) return;
    try {
      await navigator.clipboard.writeText(pairing.pairingCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied by the browser; the code is already
      // shown large on screen, so this is a soft failure, not an error state.
    }
  }

  return (
    <Card dir={catalog.dir}>
      <CardBody>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="size-4 text-primary" /> {t.title}
        </CardTitle>
        <p className="mt-2 text-xs text-muted">{t.description}</p>

        {denial && (
          <InfoBanner variant="danger" className="mt-3">
            {denial.error}
            <SupportDetails reference={denial.supportReference} capability={denial.capability} />
          </InfoBanner>
        )}

        {(phase === "idle" || phase === "loading" || phase === "error") && (
          <Button className="mt-3" onClick={handlePair} loading={phase === "loading"}>
            {phase === "loading" ? t.buttonBusy : t.button}
          </Button>
        )}

        {pairing && (phase === "ready" || phase === "expired") && (
          <div className="mt-4 flex flex-wrap items-start gap-5" data-testid="pair-signer-device-code">
            <PairingQrCode value={pairing.pairingCode} />
            <div className="min-w-[180px]">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted">{t.codeLabel}</div>
              <div
                dir="ltr"
                data-testid="pair-signer-device-pairing-code"
                className="mt-1 font-mono text-[28px] font-bold tracking-[0.12em] text-ink"
              >
                {pairing.pairingCode}
              </div>
              <p className="mt-2 text-xs text-muted">{t.qrHint}</p>

              {phase === "ready" ? (
                <div className="mt-3 text-xs text-muted" data-testid="pair-signer-device-countdown">
                  {t.expiresLabel} <span className="font-mono font-semibold text-ink">{formatCountdown(remaining)}</span>
                </div>
              ) : (
                <div className="mt-3" data-testid="pair-signer-device-expired">
                  <div className="text-xs font-semibold text-danger">{t.expiredLabel}</div>
                  <p className="mt-1 text-xs text-muted">{t.expiredHint}</p>
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                {phase === "ready" ? (
                  <Button size="sm" variant="secondary" onClick={handleCopy}>
                    {copied ? t.copied : t.copy}
                  </Button>
                ) : (
                  <Button size="sm" onClick={handlePair}>
                    {t.regenerate}
                  </Button>
                )}
                {/* Secondary download action under the QR code. The APK is
                    served same-origin at the downloads route — NOT under
                    /v1/signer/ — so it's a plain relative link, no auth token. */}
                <a
                  href="/v1/downloads/signer-app/android/latest.apk"
                  className={buttonVariants({ variant: "secondary", size: "sm" })}
                  data-testid="pair-signer-device-android-link"
                >
                  {t.getAndroidApp}
                </a>
              </div>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default PairSignerDevice;
